import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Package,
    Truck,
    CheckCircle2,
    Clock,
    XCircle,
    Eye,
    RefreshCw,
    ShoppingCart,
    Calendar,
    Box,
    Loader2
} from "lucide-react";
import {
    Dialog,
    DialogContent,
} from "@/components/ui/dialog";
import { useCart } from "../../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
    pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
    confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
    processing: { label: "Processing", color: "bg-purple-100 text-purple-800", icon: Package },
    shipped: { label: "Shipped", color: "bg-indigo-100 text-indigo-800", icon: Truck },
    delivered: { label: "Delivered", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
    cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: XCircle }
};

export default function ResellerMyOrders() {
    const [searchParams, setSearchParams] = useSearchParams();
    const paymentIntentId = searchParams.get("payment_intent");

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const { clearCart } = useCart();

    const pollPaymentStatus = async (piId, count = 0) => {
        if (count >= 15) {
            setVerifying(false);
            toast.error("Payment verification timed out. Please refresh in a moment.");
            fetchMyOrders();
            return;
        }

        try {
            const response = await axios.get(`${API}/checkout/status/${piId}`);
            if (response.data?.payment_status === "paid") {
                setVerifying(false);
                toast.success("Payment confirmed successfully!");
                clearCart();
                // Clear the query param
                const newParams = new URLSearchParams(searchParams);
                newParams.delete("payment_intent");
                setSearchParams(newParams);
                fetchMyOrders();
            } else {
                setTimeout(() => pollPaymentStatus(piId, count + 1), 2000);
            }
        } catch (error) {
            console.error("Poll error:", error);
            // Don't stop polling on single error unless it's a 404/permanent
            setTimeout(() => pollPaymentStatus(piId, count + 1), 2000);
        }
    };

    const fetchMyOrders = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/reseller/my-orders`);
            const data = response.data;
            if (Array.isArray(data)) {
                setOrders(data);
            } else if (data && typeof data === 'object' && Array.isArray(data.orders)) {
                setOrders(data.orders);
            } else {
                setOrders([]);
            }
        } catch (error) {
            console.error("Fetch my orders error:", error);
            toast.error("Failed to load your orders");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (paymentIntentId) {
            setVerifying(true);
            pollPaymentStatus(paymentIntentId);
        } else {
            fetchMyOrders();
        }
    }, [paymentIntentId]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">My Purchases</h1>
                    <p className="text-slate-500">View products you've bought from the Funeral Supplies</p>
                </div>
                <Button onClick={fetchMyOrders} variant="outline" size="sm" className="gap-2 rounded-xl">
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </Button>
            </div>

            <div className="card-elevated overflow-hidden bg-white/80 backdrop-blur-sm border border-indigo-100/50">
                {verifying ? (
                    <div className="p-12 text-center">
                        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
                        <p className="text-slate-500 font-medium">Verifying your payment...</p>
                        <p className="text-sm text-slate-400 mt-1">Please don't close this page.</p>
                    </div>
                ) : loading ? (
                    <div className="p-12 text-center">
                        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
                        <p className="text-slate-500 font-medium">Loading your orders...</p>
                    </div>
                ) : orders.length === 0 ? (
                    <div className="p-12 text-center">
                        <ShoppingCart className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500 font-medium">No purchases yet</p>
                        <p className="text-sm text-slate-400 mt-1">Products you buy for your business will appear here.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50/50 text-slate-500 font-medium text-xs uppercase tracking-widest border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-4">Order ID</th>
                                    <th className="px-6 py-4">Date</th>
                                    <th className="px-6 py-4">Items</th>
                                    <th className="px-6 py-4">Total</th>
                                    <th className="px-6 py-4 text-center">Status</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {orders.map((order) => (
                                    <tr key={order.order_id} className="hover:bg-slate-50/30 transition-colors group">
                                        <td className="px-6 py-4">
                                            <p className="font-mono font-bold text-slate-900">#{order.order_id.slice(-8).toUpperCase()}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-slate-600 flex items-center gap-1">
                                                <Calendar className="w-3 h-3" /> {formatDate(order.created_at)}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-slate-600 font-medium">{order.items?.length || 0} items</p>
                                        </td>
                                        <td className="px-6 py-4 font-bold text-slate-900">
                                            ${(order.total_charged || order.total || 0).toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <Badge className={`rounded-full shadow-none ${statusConfig[order.status]?.color || "bg-slate-100"}`}>
                                                {statusConfig[order.status]?.label || order.status}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="rounded-lg hover:bg-white hover:shadow-sm"
                                                onClick={() => { setSelectedOrder(order); setShowDetails(true); }}
                                            >
                                                <Eye className="w-4 h-4 mr-2" />
                                                Details
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Basic Details Modal (Reuse or simplify existing order details logic) */}
            <Dialog open={showDetails} onOpenChange={setShowDetails}>
                <DialogContent className="max-w-2xl overflow-y-auto max-h-[90vh]">
                    {selectedOrder && (
                        <div className="space-y-6 p-2">
                            <div className="border-b pb-4">
                                <h3 className="text-xl font-bold">Order #{selectedOrder.order_id.slice(-8).toUpperCase()}</h3>
                                <p className="text-sm text-slate-500">{formatDate(selectedOrder.created_at)}</p>
                            </div>

                            <div className="space-y-4">
                                <h4 className="font-bold text-sm uppercase tracking-wider text-slate-400">Items</h4>
                                {selectedOrder.items?.map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                                        <div className="w-12 h-12 bg-white rounded-lg flex-shrink-0 border flex items-center justify-center">
                                            {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-cover" /> : <Box size={20} />}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-bold">{item.name}</p>
                                            <p className="text-xs text-slate-500">Qty: {item.quantity} • ${item.display_price?.toFixed(2)}</p>

                                            {/* Customizations display */}
                                            {item.customizations && (Array.isArray(item.customizations) ? item.customizations.length > 0 : Object.keys(item.customizations).length > 0) && (
                                                <div className="mt-2 space-y-1 pl-2 border-l border-slate-200 py-0.5">
                                                    {Array.isArray(item.customizations) ? (
                                                        item.customizations.map((cust) => (
                                                            <div key={cust.id} className="text-[10px]">
                                                                <div className="flex justify-between font-bold text-slate-600">
                                                                    <span>{cust.name}</span>
                                                                    <span className="text-indigo-600 ml-2">+${cust.total.toFixed(2)}</span>
                                                                </div>
                                                                {cust.fields.map((f, i) => (
                                                                    <p key={i} className="text-slate-400 italic leading-tight">
                                                                        {f.label}: {f.value}
                                                                    </p>
                                                                ))}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        Object.entries(item.customizations).map(([label, val]) => (
                                                            <p key={label} className="text-[10px] text-slate-400 italic">
                                                                <span className="font-medium mr-1 text-slate-500">{label}:</span>
                                                                {val && typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))
                                                                    ? new Date(val).toLocaleDateString()
                                                                    : val === true ? 'Yes' : val}
                                                            </p>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="border-t pt-4 flex justify-between items-center">
                                <span className="font-bold">Total Paid</span>
                                <span className="text-lg font-black">${(selectedOrder.total || 0).toFixed(2)}</span>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
